import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CircleXIcon } from 'lucide-react'
import type { EmployeeDTO } from '@deepguide-ai/types'
import { trpc } from '@/lib/trpc'
import {
  Combobox,
  ComboboxContent,
  ComboboxCreateNew,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@deepguide-ai/ui/combobox'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { cn } from '@/lib/utils'

export interface ManagerSelection {
  /** Directory link when the manager is an existing employee, else null. */
  managerEmployeeId: string | null
  /** Display name — the employee's name when linked, or free text otherwise. */
  manager: string | null
}

interface ManagerSelectProps {
  /** The employee being edited — excluded so nobody manages themselves. */
  excludeEmployeeId?: string
  managerEmployeeId: string | null
  managerName: string | null
  onChange: (next: ManagerSelection) => void
  disabled?: boolean
  id?: string
}

function displayName(e: Pick<EmployeeDTO, 'firstName' | 'lastName' | 'email'>) {
  return [e.firstName, e.lastName].filter(Boolean).join(' ').trim() || e.email
}

/**
 * Manager picker over the workspace directory. Selecting an employee writes a
 * structured `managerEmployeeId` (and mirrors their name into `manager`);
 * typing a name that isn't in the directory keeps `manager` as free text with
 * no link — so IdP/HRIS-synced manager names (and external managers) survive.
 */
export function ManagerSelect({
  excludeEmployeeId,
  managerEmployeeId,
  managerName,
  onChange,
  disabled,
  id,
}: ManagerSelectProps) {
  // Target user is a 5-50 person company; a single page covers the directory.
  const { data } = useQuery(trpc.employee.list.queryOptions({ limit: 100 }))

  const candidates = useMemo(
    () => (data?.items ?? []).filter((e) => e.id !== excludeEmployeeId),
    [data?.items, excludeEmployeeId],
  )

  const selected = useMemo(
    () =>
      managerEmployeeId
        ? candidates.find((e) => e.id === managerEmployeeId)
        : undefined,
    [candidates, managerEmployeeId],
  )

  // Prefer the resolved employee's name; fall back to the mirrored free text
  // (covers a linked manager who isn't on this page, or an unlinked name).
  const triggerLabel = selected ? displayName(selected) : managerName?.trim()
  const hasValue = Boolean(managerEmployeeId || managerName?.trim())

  const handleSelect = (value: string) => {
    const emp = candidates.find((e) => e.id === value)
    if (emp) {
      onChange({ managerEmployeeId: emp.id, manager: displayName(emp) })
    }
    // A non-matching value comes from the free-text row and is handled by
    // ComboboxCreateNew's onCreateNew below — ignore it here.
  }

  return (
    <div className="relative">
      <Combobox
        data={candidates.map((e) => ({ label: displayName(e), value: e.id }))}
        type="manager"
        modal
        value={managerEmployeeId ?? ''}
        onValueChange={handleSelect}
      >
        <ComboboxTrigger
          id={id}
          className={cn(
            'w-full justify-between font-normal',
            hasValue && !disabled && 'pr-10',
          )}
          disabled={disabled}
        >
          {triggerLabel ? (
            <span className="flex min-w-0 items-center gap-2">
              {selected ? (
                <MemberAvatar person={{ name: triggerLabel, email: selected.email }} size="sm" />
              ) : null}
              <span className="truncate">{triggerLabel}</span>
              {!managerEmployeeId ? (
                <span className="shrink-0 text-xs text-muted-foreground">
                  (not in directory)
                </span>
              ) : null}
            </span>
          ) : (
            <span className="text-muted-foreground">Select manager…</span>
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <ComboboxInput placeholder="Search or type a name…" />
          <ComboboxList>
            <ComboboxEmpty>No matching employee.</ComboboxEmpty>
            <ComboboxGroup>
              {candidates.map((e) => (
                <ComboboxItem
                  key={e.id}
                  value={e.id}
                  keywords={[displayName(e), e.email]}
                  className={cn(
                    'flex items-center gap-2',
                    e.id === managerEmployeeId && 'bg-selected',
                  )}
                >
                  <MemberAvatar person={{ name: displayName(e), email: e.email }} size="sm" />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{displayName(e)}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {e.email}
                    </span>
                  </span>
                </ComboboxItem>
              ))}
            </ComboboxGroup>
            {/* Keep whatever the user typed as a free-text (unlinked) manager. */}
            <ComboboxCreateNew
              onCreateNew={(value) =>
                onChange({ managerEmployeeId: null, manager: value })
              }
            >
              {(inputValue) => (
                <>
                  <span className="text-muted-foreground">Use name:</span>
                  <span className="truncate">&ldquo;{inputValue}&rdquo;</span>
                </>
              )}
            </ComboboxCreateNew>
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {hasValue && !disabled ? (
        <button
          type="button"
          aria-label="Clear manager"
          title="Clear manager"
          className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground"
          onClick={() =>
            onChange({ managerEmployeeId: null, manager: null })
          }
        >
          <CircleXIcon className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  )
}
